// 👇 Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// 👇 Axios Imports
import axios from 'axios'

import {urlVesselVoyageService} from '@src/endpoints'
import { getUriParams } from '@utils'

// 👇 Third-Party component
import toast from 'react-hot-toast'

// 👇 Components
import { ToastContent } from "@src/views/components/toast/ToastContent"

const ToastMessage = (heading, message, isSuccess) => {
  toast(t => (
    <ToastContent t={t} heading = {heading} response = {isSuccess} message = {message} />
  ))
}
export const getAllData = createAsyncThunk('vesselVoyage/getAllData', async () => {
  const response = await axios.get(`${urlVesselVoyageService}/GetVesselVoyageCount`)
  return {
    data: response.data,
    loadedAll: true
  }
})

export const getData = createAsyncThunk('vesselVoyage/getData', async data => {
  const {params, filterForm} = data
  const uri = getUriParams({...params, status:"", q:""})
  const filter = {id: "", terminalName: params.terminalName, ...filterForm}
  // Pass IsManual only when explicitly set (null means "all")
  if (filterForm.isManual !== null && filterForm.isManual !== undefined) {
    filter.IsManual = filterForm.isManual
  }
  const response = await axios.post(`${urlVesselVoyageService}/GetAllVoyage?${uri}`, filter)
    return {
    params,
    terminal: [],
    data: response.data.data || [],
    totalPages: response.data.totalCount,

    loaded: true,
    filterForm
  }
})

export const uploadExcelData = async (data) => {
  const {excelData} = data
  const res = await axios.post(`${urlVesselVoyageService}/VesselVoyageExcelUpload`, excelData)
    if (res.status === 200) {
    return {success: true}
  } else {
    return {success: false}
  }
}

export const addVoyage = createAsyncThunk('vesselVoyage/addVoyage', async (data, { dispatch, rejectWithValue }) => {
  const {voyage, params, filterForm} = data
  const response = await axios.post(`${urlVesselVoyageService}/AddVoyage`, {...voyage, id: "", IsManual: voyage.isManual ?? false})
  const body = response?.data
  if (!body?.success) {
    let reasons = ['Voyage was not saved']
    if (body?.errors?.length) {
      reasons = body.errors
    } else if (body?.messages?.length) {
      reasons = body.messages
    }
    return rejectWithValue(reasons)
  }
  dispatch(getData({params, filterForm}))
  return body.data ?? voyage
})

export const getManualVoyageSyncCandidates = createAsyncThunk('vesselVoyage/getSyncCandidates', async (requestBody) => {
  const response = await axios.post(`${urlVesselVoyageService}/ManualVoyageSyncCandidates`, requestBody)
  return response.data.data || []
})

export const getManualVoyageSyncPreview = createAsyncThunk('vesselVoyage/getSyncPreview', async (requestBody) => {
  const response = await axios.post(`${urlVesselVoyageService}/ManualVoyageSyncPreview`, requestBody)
  return response.data.data || []
})

export const executeManualVoyageSync = createAsyncThunk('vesselVoyage/executeSync', async (data, { dispatch }) => {
  const { requestBody, params, filterForm } = data
  const response = await axios.post(`${urlVesselVoyageService}/ManualVoyageSyncReplacement`, requestBody)
  dispatch(getData({ params, filterForm }))
  return response.data
})

export const deleteVoyage = createAsyncThunk('vesselVoyage/deleteVoyage', async (data, { dispatch }) => {
  const {id, params, filterForm} = data
  const response = await axios.delete(`${urlVesselVoyageService}/DeleteVoyage/${id}`)
   response.data.success === true ? ToastMessage('Promotion', "The Vessel Schedule Deleted Sucessfully", true) : ToastMessage('Promotion', "Vessel Schedule Cannot be deleted", false)
  dispatch(getData({params, filterForm}))
  return id
})

export const vesselVoyageSlice = createSlice({
  name: 'vesselVoyage',
  initialState: {
    loaded : false,
    loadedAll: false,
    loadedFFR: false,
    data: [],
    terminal: {},
    total: 1,
    params: {},
    allData: {},
    status: [],
    notes: [],
    allTimeSheetUser:[],
    allContacts:[],
    filterForm: {},
    vesselNames:[],
    syncCandidates: [],
    syncCandidatesLoaded: false,
    syncPreview: [],
    syncPreviewLoaded: false,
    syncExecuting: false
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllData.pending, (state) => {
        state.loadedAll = false
      })
      .addCase(getAllData.fulfilled, (state, action) => {
        state.loadedAll = action.payload.loadedAll
        state.allData = action.payload.data
      })
      .addCase(getData.pending, (state) => {
        state.loaded = false
      })
      .addCase(getData.fulfilled, (state, action) => {
        state.loaded = action.payload.loaded
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.totalPages
        state.terminal = action.payload.terminal
        state.filterForm = action.payload.filterForm
      })
      .addCase(getManualVoyageSyncCandidates.pending, (state) => {
        state.syncCandidatesLoaded = false
      })
      .addCase(getManualVoyageSyncCandidates.fulfilled, (state, action) => {
        state.syncCandidates = action.payload
        state.syncCandidatesLoaded = true
      })
      .addCase(getManualVoyageSyncCandidates.rejected, (state) => {
        state.syncCandidatesLoaded = true
      })
      .addCase(getManualVoyageSyncPreview.pending, (state) => {
        state.syncPreviewLoaded = false
      })
      .addCase(getManualVoyageSyncPreview.fulfilled, (state, action) => {
        state.syncPreview = action.payload
        state.syncPreviewLoaded = true
      })
      .addCase(getManualVoyageSyncPreview.rejected, (state) => {
        state.syncPreviewLoaded = true
      })
      .addCase(executeManualVoyageSync.pending, (state) => {
        state.syncExecuting = true
      })
      .addCase(executeManualVoyageSync.fulfilled, (state) => {
        state.syncExecuting = false
        state.syncPreview = []
        state.syncCandidates = []
      })
      .addCase(executeManualVoyageSync.rejected, (state) => {
        state.syncExecuting = false
      })
      .addMatcher(
        action => action.type.endsWith('/pending') &&
          action.type !== getManualVoyageSyncCandidates.pending.type &&
          action.type !== getManualVoyageSyncPreview.pending.type &&
          action.type !== executeManualVoyageSync.pending.type,
        (state) => { state.loaded = false }
      )
      //reset loaded to true if rejected
      .addMatcher(
        action => action.type.endsWith('/rejected') &&
          action.type !== getManualVoyageSyncCandidates.rejected.type &&
          action.type !== getManualVoyageSyncPreview.rejected.type &&
          action.type !== executeManualVoyageSync.rejected.type,
        (state) => { state.loaded = true }
      )
  }
})

export default vesselVoyageSlice.reducer

vesselVoyageSlice.metadata = {
  name: 'Zakeer Sainalabdeen',
  github: 'zakeerwtc',
  description: 'VesselVoyage - Store {Get Data from Microservices}',
  filename: 'index',
  githubFile: 'https://github.com/WTCGroup/Frontend/blob/main/src/views/hrm/time-sheet/store/index.js',
  prodUrl: ''
}
